<template>
  <VContainer fluid>
    <VRow>
      <VCol cols="12">
        <VCard :loading="loading" class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />

            <div class="d-flex ga-2">
              <VTooltip text="Fermer" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="cancel">
                    <VIcon icon="mdi-close-circle-outline" />
                  </VBtn>
                </template>
              </VTooltip>

              <!-- Save -->
              <VTooltip text="Sauvegarder" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="save">
                    <VIcon icon="mdi-content-save-all" />
                  </VBtn>
                </template>
              </VTooltip>

              <!-- Remove -->
              <VTooltip v-if="canRemove" text="Supprimer" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="remove">
                    <VIcon icon="mdi-trash-can-outline" />
                  </VBtn>
                </template>
              </VTooltip>
            </div>
          </v-toolbar>

          <VCardText>
            <!-- Row 1 -->
            <VRow dense>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.dureeDuContrat"
                  type="number"
                  suffix="ans"
                  class="ms-5"
                  :rules="rules.isNotEmpty"
                >
                  <template #label>
                    Durée du Contrat<span class="text-red"><strong> *</strong></span>
                  </template>
                </VTextField>
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="dateDeDemarrageDisplay"
                  v-maska="'##/##/####'"
                  placeholder="JJ/MM/AAAA"
                  prepend-inner-icon="mdi-calendar"
                  class="ms-5"
                  :rules="rules.date"
                >
                  <template #label>
                    Date de Démarrage<span class="text-red"><strong> *</strong></span>
                  </template>
                </VTextField>
              </VCol>
            </VRow>

            <!-- Row 2: Tarif -->
            <VRow dense>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifLavage"
                  type="number"
                  prefix="€"
                  label="Tarif Lavage"
                  class="ms-5"
                />
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifSechage"
                  type="number"
                  prefix="€"
                  label="Tarif Séchage"
                />
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifLessive"
                  type="number"
                  prefix="€"
                  label="Tarif Lessive"
                  class="me-5"
                />
              </VCol>
            </VRow>

            <!-- Observation -->
            <VRow dense>
              <VCol cols="12">
                <VTextarea
                  v-model="avenant.observation"
                  label="Observation"
                  class="mx-5"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog
      v-model="removeAvenantDialog"
      :dialog-title="dialogTitle"
      :dialog-text="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
    />
  </VContainer>
</template>

<script setup lang="ts">
import { reactive, ref, computed, toRef, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import type { Avenant } from "@/types";
import {
  deleteAvenant,
  getAvenantById,
  postAvenant,
  putAvenant,
} from "@/utils/avenant-api";
import { isNotEmpty, validateDate } from "@/utils/validation-util";
import { useFormattedDate } from "@/composables/useFormattedDate";

/* ---------- Router & Store ---------- */
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

/* ---------- State ---------- */
const loading = ref(false);
const removeAvenantDialog = ref(false);
const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer cet avenant ?";
const title = ref("");

/* ---------- Form Model ---------- */
const avenant = reactive<Avenant>({} as Avenant);

/* ---------- Validation ---------- */
const rules = {
  isNotEmpty: [(v: any) => isNotEmpty(v) || "Champ requis"],
  date: [(v: string) => validateDate(v) || "Date invalide"],
};

/* ---------- Date Bridge (maskable) ---------- */
const dateDeDemarrageDisplay = useFormattedDate(toRef(avenant, "dateDeDemarrage"));

/* ---------- Computed ---------- */
const canRemove = computed(() => !!avenant.id);

/* ---------- Actions ---------- */
function cancel() {
  router.push({
    name: "laverie",
    params: { id: route.params.laverieId },
  });
}

function remove() {
  removeAvenantDialog.value = true;
}

function onCancel() {
  removeAvenantDialog.value = false;
}

function isCreatingNewAvenant(): boolean {
  return route.name === "newavenant" || route.params.id === "new";
}

async function onConfirm() {
  try {
    loading.value = true;
    await deleteAvenant(avenant.id!);
    cancel();
    appStore.sendSuccessNotice("Avenant supprimé.");
  } catch {
    appStore.sendErrorNotice("Opération échouée.");
  } finally {
    loading.value = false;
    removeAvenantDialog.value = false;
  }
}

async function save() {
  try {
    loading.value = true;
    if (isCreatingNewAvenant()) {
      const created = await postAvenant(avenant, route.params.laverieId as string);
      Object.assign(avenant, created);

      router.replace({
        name: "avenant",
        params: { laverieId: route.params.laverieId, id: created.id },
      });

      title.value = "Editer l'avenant";
      appStore.sendSuccessNotice("Avenant créé.");
    } else {
      await putAvenant(avenant);
      appStore.sendSuccessNotice("Avenant mis à jour.");
    }
  } finally {
    loading.value = false;
  }
}

/* ---------- Lifecycle ---------- */
onMounted(async () => {
  title.value = isCreatingNewAvenant() ? "Nouveau avenant" : "Editer l'avenant";

  if (!isCreatingNewAvenant()) {
    Object.assign(avenant, await getAvenantById(route.params.id as string));
  }
});
</script>

<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
